/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild
} from '@angular/core';
import {
  FormGroup,
  FormControl,
  Validators,
  AbstractControl
} from '@angular/forms';
import { Router } from '@angular/router';
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  EMPTY,
  finalize,
  map,
  Observable,
  of,
  switchMap,
  tap
} from 'rxjs';
import { BaseComponent } from '~backoffice/app/~shared/abstract-components/base.component';
import { InvoiceFacade } from '~backoffice/app/~store/facades/invoice.facade';
import {
  BillingType,
  CreateInvoiceParams,
  CreateInvoiceParamsCustomer,
  InvoicePaymentMethod,
  InvoiceType
} from '~core/models/invoice.models';
import { LoadListener } from '~backoffice/app/~shared/utils/rxjs';
import {
  BillingAddress,
  BillingAddressFormValue,
  CreateCustomerSupplierParams,
  Currency,
  CustomerSupplier
} from '~core/models/customer-supplier.model';
import { InvoiceService } from '~core/services/invoice.service';
import { AuthFacade } from '~backoffice/app/~store/facades/auth.facade';
import { ROUTES } from '~backoffice/app/~shared/constants/routes.constants';
import { CompanyService } from '~core/services/company.service';
import { Company } from '~core/models/company.models';
import {
  phoneNumberValidator,
  validatePhoneNumber
} from '~backoffice/app/~shared/utils/validators';
import {
  getFlagEmoji,
  parseMaskToNumber,
  removeNilProperties
} from '~backoffice/app/~shared/utils/utils';
import { CountryCode } from 'libphonenumber-js';
import { Country } from '~core/models/shared.models';
import { ReferenceData } from '~core/models/reference-data.models';
import { ReferenceDataService } from '~core/services/reference-data.service';
import intlTelInput from 'intl-tel-input';
import { Pagination } from '~core/models/core.models';
import { BillingAddressService } from '~core/services/billing-address.service';
import { CreateBillingAddressParams } from '~core/models/billing-address.model';
import { toISOUTCWithOffset } from '~backoffice/app/~shared/utils/date.utils';
import { attachDialCodePrefill } from '~backoffice/app/~shared/utils/phone.utils';
import { Establishment } from '~core/models/establishment.model';
import { SalesPoint } from '~core/models/sales-point.models';
import { EstablishmentService } from '~core/services/establishment.service';
import { SalesPointService } from '~core/services/sales-point.service';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OrganizationService } from '~backoffice/app/~shared/services/organization.service';
import { CustomerSupplierService } from '~backoffice/app/~shared/services/customer-supplier.service';

@Component({
  selector: 'invoice-form',
  templateUrl: './invoice-form.component.html',
  styleUrl: './invoice-form.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvoiceFormComponent
  extends BaseComponent
  implements OnInit, AfterViewInit
{
  private readonly _router = inject(Router);
  private _invoiceFacade = inject(InvoiceFacade);
  protected _authFacade = inject(AuthFacade);
  private _organizationService = inject(OrganizationService);
  private _customerSupplierService = inject(CustomerSupplierService);
  private _establishmentService = inject(EstablishmentService);
  private _salesPointService = inject(SalesPointService);
  private _invoiceService = inject(InvoiceService);
  private _companyService = inject(CompanyService);
  private _referenceDataService = inject(ReferenceDataService);
  private _billingAddressService = inject(BillingAddressService);

  currentOrganization = toSignal(
    this._authFacade.currentUser$.pipe(map(user => user?.organization ?? null)),
    { initialValue: null }
  );

  partners = signal<Partial<Company | CustomerSupplier>[]>([]);
  selectedPartner = signal<Partial<Company | CustomerSupplier> | null>(null);

  billingAddress = signal<BillingAddress | null>(null);
  currentSupplier: CustomerSupplier | null = null;

  establishmentsListener: LoadListener<Establishment[]> | undefined;
  salePointsListener: LoadListener<SalesPoint[]> | undefined;
  countriesListener: LoadListener<Country[]> | undefined;
  citiesListener: LoadListener<ReferenceData[]> | undefined;
  districtsListener: LoadListener<ReferenceData[]> | undefined;

  isSubmitting = signal(false);
  selectedIndex = signal<number | null>(null);

  billingType = BillingType;
  invoiceTypes = Object.values(InvoiceType).filter(
    type => type !== InvoiceType.creditNote
  );

  billingCurrencies = Object.values(Currency);
  billingTypes = Object.values(BillingType);

  parseNumberFn = parseMaskToNumber;
  getFlagEmojiFn = getFlagEmoji;
  validatePhoneNumberFn = validatePhoneNumber;
  intlBillingInputPhone = viewChild<ElementRef>('billingPhoneNumberInput');
  intlPartnerInputPhone = viewChild<ElementRef>('partnerInputPhone');

  billingPhoneNumberIntlPluginRef?: intlTelInput.Plugin;
  partnerPhoneNumberIntlPluginRef?: intlTelInput.Plugin;

  paymentMethods = Object.values(InvoicePaymentMethod);
  private _hasShownNoPartnerToast = false;

  form = new FormGroup({
    type: new FormControl<string | null>(null, [Validators.required]),
    billingType: new FormControl<string | null>(null, [Validators.required]),
    paymentMethods: new FormControl<InvoicePaymentMethod[] | null>(null, [
      Validators.required
    ]),
    establishmentId: new FormControl<string | null>(null, [
      Validators.required
    ]),
    salesPointId: new FormControl<string | null>(null, [Validators.required]),
    issuedAt: new FormControl<string>(
      new Date().toISOString(),
      Validators.required
    ),
    dueDate: new FormControl<string>(''),
    conversionRate: new FormControl<number | null>(null, [
      Validators.pattern(/^\d+(\.\d+)?$/)
    ])
  });

  partnerForm = new FormGroup({
    billingCurrency: new FormControl<string>(''),
    phoneNumber: new FormControl('', {
      validators: [Validators.required]
    })
  });

  billingAddressForm = new FormGroup({
    name: new FormControl<string>('', [Validators.required]),
    email: new FormControl('', [Validators.email]),
    phoneNumber: new FormControl('', {
      validators: [
        Validators.required,
        phoneNumberValidator({
          country: this._constantsService.defaultCountry
        })
      ]
    }),
    address: new FormGroup({
      country: new FormControl<string | null>(
        this._constantsService.defaultCountry,
        [Validators.required]
      ),
      city: new FormControl<string | null>(null, [Validators.required]),
      district: new FormControl<string | null>(null, [Validators.required]),
      location: new FormControl<string>('', [Validators.required])
    }),
    customerSupplierId: new FormControl<string | null>(null),
    createdById: new FormControl<string>('')
  });

  searchControl = new FormControl('', [Validators.required]);

  violations$ = this._globalFacade.formViolations$.pipe(
    delay(500),
    tap({
      next: violations => {
        violations?.forEach(violation =>
          this.form.get(violation.propertyPath)?.setErrors({
            httpError: violation.title
          })
        );
      }
    })
  );

  ngOnInit(): void {
    this._getCurrentSupplier();
    this._handleCountryChange();
    this._handleEstablishementChange();
    this.initEstablishmentsListener();
    this.initSalePointsListener();
    this.initCountriesListener();
    this.initCitiesListener();
    this.initDistrictsListener();
    this._billingTypeChange();
    this._initSearchListener();
    this.searchControl.disable();
  }

  ngAfterViewInit(): void {
    if (!this._isPlatformBrowser) return;
    this._initPhoneNumberInputs();
  }

  selectAddress(index: number, billingAddress: BillingAddress): void {
    this.selectedIndex.set(index);
    this.billingAddress.set(billingAddress);
  }

  private _initSearchListener(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(350),
        distinctUntilChanged(),
        switchMap(value => {
          if (typeof value !== 'string') return EMPTY;
          const type = this.form.get('billingType')?.value as BillingType;
          return this._searchPartnerByValue(value, type);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  private _resetBillingAddressForm(): void {
    this.billingAddressForm.reset({
      address: {
        country: this._constantsService.defaultCountry
      }
    });
  }

  private _billingTypeChange(): void {
    this.form
      .get('billingType')
      ?.valueChanges.pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(type => {
          if (!type) {
            this.searchControl.disable();
            return;
          }
          setTimeout(() => this._initPhoneNumberInputs(), 0);
          this.searchControl.enable();
          this._loadAllPartners(type as BillingType);
          this._searchPartnerByValue(null, type as BillingType);
          this._resetBillingAddressForm();
          this.partnerForm.reset();
          this.searchControl.reset();
          this.selectedPartner.set(null);
          this.billingAddress.set(null);

          this.searchControl.reset('');

          if (type === BillingType.bToF) {
            this.conversionRateControl?.setValidators([Validators.required]);
            this.billingCurrencyControl?.setValidators([Validators.required]);
          } else {
            this.conversionRateControl?.clearValidators();
            this.billingCurrencyControl?.clearValidators();
          }

          if (type === BillingType.bToB) {
            this.searchControl.setValidators([Validators.required]);
          } else {
            this.searchControl.clearValidators();
          }

          this.searchControl.updateValueAndValidity();
        })
      )
      .subscribe();
  }

  private _loadAllPartners(type: BillingType): void {
    this._customerSupplierService
      .list(
        removeNilProperties({
          archived: false,
          type,
          createdById:
            type === BillingType.bToB
              ? ''
              : this._authFacade.currentUser?.organization.id,
          details: 'details'
        })
      )
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(result => this.partners.set(result.items))
      )
      .subscribe();
  }

  private _searchPartnerByValue(
    value: string | CustomerSupplier | Company | null,
    type: BillingType
  ): Observable<Pagination<CustomerSupplier> | Pagination<Company>> {
    const search =
      (typeof value === 'string' ? value : value?.name)?.trim() ?? '';

    if (!search) {
      this._hasShownNoPartnerToast = false;
      this.partners.set([]);
      this.selectedPartner.set(null);
      return EMPTY;
    }
    return this._customerSupplierService
      .list(
        removeNilProperties({
          archived: false,
          ...(type === BillingType.bToB
            ? { orNif: search, orName: search }
            : { name: `like(%${search}%)` }),
          type: type,
          details: 'details',
          createdById:
            type === BillingType.bToB
              ? ''
              : this._authFacade.currentUser?.organization.id
        })
      )
      .pipe(
        switchMap(result => {
          if (result.items.length > 0) {
            this._hasShownNoPartnerToast = false;
            this.partners.set(result.items);
            return EMPTY;
          }
          if (type !== BillingType.bToB) {
            this.partners.set([]);
            this.selectedPartner.set({
              billingAddresses: []
            });
            this._resetBillingAddressForm();
            this.partnerForm.reset();
            setTimeout(() => this._initPhoneNumberInputs(), 0);
            if (!this._hasShownNoPartnerToast) {
              this._hasShownNoPartnerToast = true;
              this._toastService.open({
                content:
                  'Aucun utilisateur trouvé. Veuillez remplir les informations de facturation pour continuer',
                type: 'warning'
              });
            }

            return EMPTY;
          }

          const companyParams = {
            orNif: search,
            orName: search,
            ncc: `neq(${this._authFacade.currentUser?.organization?.nif})`
          };

          return this._companyService.list(companyParams);
        }),
        tap(result => {
          if (result.items.length > 0) {
            this.partners.set(result.items);
          } else {
            this.partners.set([]);
            this.selectedPartner.set(null);
            this._resetBillingAddressForm();
            this._toastService.open({
              content:
                "Nous n'avons pas retrouvé d'entreprise correspondant à ces identifiants",
              type: 'error'
            });
          }
        })
      );
  }

  onPartnerSelected(partner: BillingAddressFormValue & CustomerSupplier): void {
    this.selectedPartner.set(partner);
    const billingAddresses = partner.billingAddresses;
    if (
      this.billingTypeValue === BillingType.bToB &&
      partner.billingAddresses?.length
    ) {
      this.selectAddress(0, billingAddresses[0]);
    }
    if (
      this.billingTypeValue !== BillingType.bToB ||
      (this.billingTypeValue === BillingType.bToB && partner?.createdBy)
    ) {
      const billingAddress = billingAddresses[0];
      if (billingAddress) {
        this.initDistrictsListener(billingAddress.address.city);
        this.billingAddressForm.patchValue(billingAddress);
      } else {
        this._resetBillingAddressForm();
      }
    }

    setTimeout(() => this._initPhoneNumberInputs(), 0);
    this.searchControl.setValue(partner.name, { emitEvent: false });
  }

  onBlur(): void {
    const selected = this.selectedPartner();
    if (!selected) {
      this.searchControl.reset();
    }
  }

  private _handleCountryChange(): void {
    const countryControl = this.billingAddressForm.get('address.country');
    const phoneControl = this.billingAddressForm.get('phoneNumber');

    if (!countryControl || !phoneControl) return;

    countryControl.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((countryCode: string | null) => {
          if (!countryCode) return;

          if (this.billingPhoneNumberIntlPluginRef) {
            this.billingPhoneNumberIntlPluginRef.setCountry(
              countryCode.toLowerCase()
            );
          }

          phoneControl.setValidators([
            Validators.required,
            phoneNumberValidator({ country: countryCode as CountryCode })
          ]);

          phoneControl.updateValueAndValidity({ emitEvent: true });
        })
      )
      .subscribe();
  }

  onSubmit(): void {
    const partner = this.selectedPartner() as Company & CustomerSupplier;
    const isNotBtoBAndNoName =
      this.billingTypeValue !== this.billingType.bToB && !partner?.name;
    const isBtoBAndHasNCC =
      this.billingTypeValue === this.billingType.bToB && !!partner?.ncc;

    if (isNotBtoBAndNoName || isBtoBAndHasNCC) {
      this.createCustomerAndInvoice();
    } else {
      this.generateInvoice();
    }
  }

  generateInvoice(): void {
    this.isSubmitting.set(true);
    const payload = removeNilProperties(this._buildInvoiceCreationParams());
    this._invoiceService
      .create(payload as CreateInvoiceParams)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(invoice => {
          this._invoiceFacade.initDraftInvoice(invoice);
          this.isSubmitting.set(false);
          this._router.navigate([ROUTES.invoiceGeneration, invoice.id]);
          this._dialogService.closeAll();
        }),
        finalize(() => {
          this.isSubmitting.set(false);
        })
      )
      .subscribe();
  }

  createCustomerAndInvoice(): void {
    const organizationId = this._authFacade.currentUser?.organization.id;
    const partner = this.selectedPartner() as Company & CustomerSupplier;
    const phoneNumber = this.partnerForm.get('phoneNumber')?.value;
    const name = this.searchControl.value;
    const billingCurrency = this.partnerForm.get('billingCurrency')?.value;
    const payload = removeNilProperties({
      name: partner.name ?? name,
      nif: partner.ncc!,
      type: this.billingTypeValue,
      phoneNumber,
      billingCurrency: billingCurrency
    });

    if (!organizationId) return;

    this._organizationService
      .createPartner(organizationId, payload as CreateCustomerSupplierParams)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        switchMap(customer => {
          this.selectedPartner.set(customer);
          return this.addBillingAddress(customer.id);
        }),
        tap(() => {
          this.generateInvoice();
        })
      )
      .subscribe();
  }

  addBillingAddress(customerSupplierId: string): Observable<any> {
    const addressValues = this.billingAddressForm.get('address')?.value ?? {};
    const cleanedAddress = removeNilProperties(addressValues);

    const payload = removeNilProperties({
      ...this.billingAddressForm.value,
      address: cleanedAddress,
      organizationId: this._authFacade.currentUser?.organization.id,
      customerSupplierId
    });

    return this._billingAddressService.create(
      payload as CreateBillingAddressParams
    );
  }

  private _buildInvoiceCreationParams(): CreateInvoiceParams {
    const value = this.form.value;
    const billingAddressFormValue = this.billingAddressForm
      .value as BillingAddressFormValue;
    const organization = this.currentOrganization();
    const supplier = organization?.customerSupplier;
    const partner = this.selectedPartner() as CustomerSupplier;
    const billingAddress = this.billingAddress();

    if (!organization || !supplier) {
      this.isSubmitting.set(false);
      throw new Error('Organization not found');
    }

    return {
      establishmentId: value.establishmentId as string,
      salesPointId: value.salesPointId as string,
      type: value.type as InvoiceType,
      billingType: value.billingType as BillingType,
      paymentMethods: (value.paymentMethods as InvoicePaymentMethod[]) ?? [],
      issuedAt: toISOUTCWithOffset(new Date(value.issuedAt!)),
      dueDate: value.dueDate
        ? toISOUTCWithOffset(new Date(value.dueDate))
        : undefined,
      conversionRate: value.conversionRate as number,
      customer: removeNilProperties({
        id: partner.id,
        name: partner.name,
        nif: partner.nif,
        taxSystem: partner.taxSystem,
        taxCenter: partner.taxCenter,
        billingType: partner.type as BillingType,
        billingCurrency: partner.billingCurrency,
        phoneNumber:
          billingAddress?.phoneNumber ?? billingAddressFormValue.phoneNumber,
        country:
          billingAddress?.address.country ||
          billingAddressFormValue.address.country ||
          'CI',
        city:
          billingAddress?.address.city ?? billingAddressFormValue.address.city,
        district:
          billingAddress?.address.district ??
          billingAddressFormValue.address.district,
        location:
          billingAddress?.address.location ??
          billingAddressFormValue.address.location,
        organizationId: partner.organization?.id
      }) as CreateInvoiceParamsCustomer,

      supplier: {
        id: supplier.id,
        name: supplier.name,
        phoneNumber: supplier.billingAddresses[0].phoneNumber,
        billingType: supplier.type,
        nif: supplier.nif,
        country: supplier.billingAddresses[0].address.country,
        city: supplier.billingAddresses[0].address.city,
        location: supplier.billingAddresses[0].address.location!,
        district: supplier.billingAddresses[0].address.district!,
        taxSystem: supplier.taxSystem,
        taxCenter: supplier.taxCenter,
        organizationId: organization.id,
        activity: supplier.activity
      },
      footerPage: 'Merci pour votre confiance !'
    };
  }

  private _getCurrentSupplier(): void {
    const organizationId = this._authFacade.currentUser?.organization.id;
    const organization = this.currentOrganization();

    if (!organization) return;
    this._organizationService
      .listPartners(organizationId!, {
        nif: organization?.nif,
        details: 'details'
      })
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(suppliers => (this.currentSupplier = suppliers.items[0]))
      )
      .subscribe();
  }

  private _initPhoneNumberInputs(): void {
    const billingEl = this.intlBillingInputPhone()?.nativeElement;
    const partnerEl = this.intlPartnerInputPhone()?.nativeElement;

    if (!billingEl) return;
    this.billingPhoneNumberIntlPluginRef = intlTelInput(billingEl, {
      allowDropdown: true,
      formatOnDisplay: true,
      nationalMode: false,
      autoInsertDialCode: true,
      separateDialCode: true,
      initialCountry: this._constantsService.defaultCountry.toLowerCase(),
      customContainer: 'country',
      dropdownContainer: document.body
    });
    attachDialCodePrefill(billingEl, this.billingPhoneNumberIntlPluginRef);

    if (!partnerEl) return;
    this.partnerPhoneNumberIntlPluginRef = intlTelInput(partnerEl, {
      allowDropdown: true,
      formatOnDisplay: true,
      nationalMode: false,
      autoInsertDialCode: true,
      separateDialCode: true,
      initialCountry: this._constantsService.defaultCountry.toLowerCase(),
      customContainer: 'country',
      dropdownContainer: document.body
    });
    attachDialCodePrefill(partnerEl, this.partnerPhoneNumberIntlPluginRef);
  }

  private _handleEstablishementChange(): void {
    this.form.controls.establishmentId.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(establishmentId => {
          const currentSalesPointId = this.form.get('salesPointId')?.value;
          this.initSalePointsListener(
            establishmentId || undefined,
            currentSalesPointId || undefined
          );
        })
      )
      .subscribe();
  }

  initEstablishmentsListener(): void {
    const establishments$ = this._establishmentService
      .list({
        itemPerPage: 100_000_000,
        organizationId: this._authFacade.currentUser?.organization.id as string
      })
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        map(res => res.items)
      );
    this.establishmentsListener = new LoadListener(establishments$);
  }

  initSalePointsListener(
    establishmentId?: string,
    salesPointIdToSet?: string
  ): void {
    const params: any = {
      itemPerPage: 100_000_000,
      enabled: true,
      details: 'details',
      organizationId: this._authFacade.currentUser?.organization.id
    };

    const salePointsListener$ = this._salesPointService.list(params).pipe(
      takeUntilDestroyed(this._destroyRef),
      map(res => res.items),
      tap(salesPoints => {
        if (establishmentId && !salesPointIdToSet) {
          const firstSalesPoint = salesPoints.find(
            sp => sp.establishment?.id === establishmentId
          );

          if (firstSalesPoint) {
            this.salesPointIdControl?.setValue(firstSalesPoint.id, {
              emitEvent: false
            });
            this.salesPointIdControl?.setErrors(null);
          }
        }
      })
    );

    this.salePointsListener = new LoadListener(salePointsListener$);
  }

  initCountriesListener(): void {
    const countries$ = this._constantsService.getCountryList().pipe(delay(100));
    this.countriesListener = new LoadListener(countries$);
  }

  initCitiesListener(): void {
    const cities$ = this._referenceDataService
      .list({
        type: 'ref_city',
        itemPerPage: 100_000_000
      })
      .pipe(map(res => res.items));
    this.citiesListener = new LoadListener(cities$);
  }

  initDistrictsListener(parentValue?: string, resetDistrict = true): void {
    if (resetDistrict) {
      const addressGroup = this.billingAddressForm.get('address') as FormGroup;
      addressGroup.patchValue({ district: null });
    }

    const params = removeNilProperties({
      type: 'ref_district',
      parentValue: parentValue,
      itemPerPage: 100_000_000
    });

    const districts$ = parentValue
      ? this._referenceDataService.list(params).pipe(map(res => res.items))
      : of([]);

    this.districtsListener = new LoadListener(districts$);
  }

  addTagFn(name: string): { label: string; value: string } {
    return {
      label: name,
      value: name
    };
  }

  get showWarningMessage(): boolean {
    const partner = this.selectedPartner() as Company & CustomerSupplier;
    return !!(
      partner &&
      partner.ncc &&
      this.billingTypeValue === this.billingType.bToB
    );
  }

  get hasBillingAddress(): boolean {
    const partner = this.selectedPartner() as Company & CustomerSupplier;

    return !!(
      partner?.billingAddresses?.length &&
      partner?.type === this.billingType.bToB &&
      partner?.organization
    );
  }

  get isSubmitDisabled(): boolean {
    const partner = this.selectedPartner() as CustomerSupplier | null;
    const billingFormValid = this.billingAddressForm.valid;
    const partnerFormValid = this.partnerForm.valid;
    const formValid = this.form.valid;

    if (!partner) return true;

    if (!partner.name && this.billingTypeValue !== BillingType.bToB) {
      return !formValid || !partnerFormValid || !billingFormValid;
    }

    if (
      partner.billingAddresses?.length &&
      this.billingTypeValue === BillingType.bToB
    ) {
      return !this.billingAddress() || !formValid;
    }

    return !formValid || !billingFormValid;
  }

  get billingTypeValue(): BillingType | null {
    return this.form.get('billingType')?.value as BillingType;
  }

  get salesPointIdControl(): AbstractControl | null {
    return this.form.get('salesPointId');
  }

  get conversionRateControl(): AbstractControl | null {
    return this.form.get('conversionRate');
  }

  get billingCurrencyControl(): AbstractControl | null {
    return this.partnerForm.get('billingCurrency');
  }

  get phoneNumberControl(): AbstractControl | null {
    return this.billingAddressForm.get('address.phoneNumber');
  }

  get searchControlTitle(): string {
    const type = this.billingTypeValue;

    switch (type) {
      case this.billingType.bToB:
        return 'NCC ou Raison Sociale';

      case this.billingType.bToC:
        return 'Nom et Prénom(s)';

      case this.billingType.bToG:
        return "Nom de l'entité";

      case this.billingType.bToF:
        return 'Raison Sociale';

      default:
        return 'Raison Sociale';
    }
  }

  get searchControlPlaceholder(): string {
    const type = this.billingTypeValue;

    switch (type) {
      case this.billingType.bToB:
        return 'Veuillez saisir le NCC ou la raison sociale pour retrouver l’entreprise';

      case this.billingType.bToC:
        return 'Veuillez saisir le nom et prénom(s)';

      case this.billingType.bToG:
        return "Veuillez saisir le nom de l'entité";

      case this.billingType.bToF:
        return 'Veuillez saisir la raison sociale';

      default:
        return 'Veuillez saisir la raison sociale';
    }
  }

  get modalTitle(): string {
    return 'Créer une nouvelle facture';
  }

  get modalDescription(): string {
    return 'Commencez par renseigner le type de facture (Hors avoir sur facture) et les informations de votre client';
  }
}
